import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import type { DeletionRecord } from '@/hooks/useTenantDeletionMutations';
import { useQuery } from '@/utils/api';

/**
 * Tenant-deletion read hooks (Praetorian-Admin-only cross-tenant status).
 *
 * Contract (frozen, see architecture.md "RESOLVED API contract"):
 * - GET /account/deletion       → 200 {deletions: TenantDeletion[]} (no allAccounts param)
 * - GET /account/deletion/{id}  → 200 {deletion, certificateUrl?} (id = URL-escaped record key)
 *
 * Both are global admin reads targeting no specific tenant, so they use
 * `useAxios({ doNotImpersonate: true })` (no `account` impersonation header).
 * Query keys come from the shared `getQueryKey` factory so F1's mutations can
 * invalidate the list on success. Backend refusals are surfaced verbatim via
 * `getErrorFromResponse: true`.
 */

interface DeletionListResponse {
  deletions: DeletionRecord[];
}

interface DeletionDetailResponse {
  deletion: DeletionRecord;
  /** Presigned URL, present only when the deletion is completed. */
  certificateUrl?: string;
}

/**
 * GET /account/deletion → the full cross-tenant deletion list.
 *
 * The endpoint is Praetorian-SuperAdmin-only (backend C1) and 403s for everyone
 * else, so callers MUST pass `enabled` explicitly (e.g.
 * `{ enabled: isPraetorianUser }`) to avoid firing a request that can only fail
 * for non-SuperAdmin users.
 */
export function useTenantDeletions(options: { enabled: boolean }) {
  const axios = useAxios({ doNotImpersonate: true });

  return useQuery({
    queryKey: getQueryKey.getTenantDeletions(),
    enabled: options.enabled,
    defaultErrorMessage: 'Failed to load tenant deletions',
    getErrorFromResponse: true,
    queryFn: async () => {
      const { data } =
        await axios.get<DeletionListResponse>('/account/deletion');
      return data.deletions;
    },
  });
}

/**
 * GET /account/deletion/{id} → a single deletion record plus its certificate URL
 * (when completed). `id` is the raw `DeletionRecord.key`; it is URL-escaped here.
 */
export function useTenantDeletion(id: string) {
  const axios = useAxios({ doNotImpersonate: true });

  return useQuery({
    queryKey: getQueryKey.getTenantDeletion(id),
    enabled: Boolean(id),
    defaultErrorMessage: 'Failed to load tenant deletion',
    getErrorFromResponse: true,
    queryFn: async () => {
      const { data } = await axios.get<DeletionDetailResponse>(
        `/account/deletion/${encodeURIComponent(id)}`
      );
      return data;
    },
  });
}
